import PropTypes from 'prop-types'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { object, string } from 'yup'

import Button from '@/components/Button'
import DeleteConfirmationOverlay from '@/components/DeleteConfirmationOverlay'
import Form, { Select, Text, Textarea } from '@/components/Form'
import {
  AZURE,
  IDP_NAME,
  OKTA,
  OTHER,
  setFieldValuesForIdentityProvider,
  shouldAutoFillOtherFields,
  validateXML
} from '@/pages/OrganizationSecurityPage/utils'

import messages from './messages'

function EditSamlIdentityProvider(props: any) {
  const intl = useIntl()

  const schema = object().shape({
    idpName: string()
      .trim()
      // @ts-expect-error
      .required(<FormattedMessage {...messages.requiredError} />),
    metadata: string()
      .trim()
      // @ts-expect-error
      .required(<FormattedMessage {...messages.requiredError} />)
      // @ts-expect-error
      .test('invalidXML', <FormattedMessage {...messages.metadataInvalidXml} />, validateXML),
    emailAttribute: string()
      .trim()
      // @ts-expect-error
      .required(<FormattedMessage {...messages.requiredError} />),
    usernameAttribute: string()
      .trim()
      // @ts-expect-error
      .required(<FormattedMessage {...messages.requiredError} />),
    familyNameAttribute: string()
      .trim()
      // @ts-expect-error
      .required(<FormattedMessage {...messages.requiredError} />)
  })

  const [state, _setState] = useState({
    showDeleteConfirmationOverlay: false,
    disabled: props.identityProvider.idpName !== 'Other',
    activeField: ''
  })
  const setState = (update: Partial<Parameters<typeof _setState>[0]>) => {
    return _setState(state => ({ ...state, ...(typeof update === 'function' ? update(state) : update) }))
  }

  const onUpdate = (values: any) => {
    props.onUpdate(values)
  }

  const onDelete = () => {
    props.onDelete(props.identityProvider.id)
  }

  const onCancel = () => {
    props.onCancel()
  }

  const showDeleteConfirmation = () => {
    setState({ showDeleteConfirmationOverlay: true })
  }

  const hideDeleteConfirmation = () => {
    setState({ showDeleteConfirmationOverlay: false })
  }

  const setFieldsForProvider = (values: any) => {
    // When an identity provider is selected, update the other fields accordingly
    if (state.activeField === IDP_NAME) {
      setFieldValuesForIdentityProvider(values)
      setState({ disabled: shouldAutoFillOtherFields(values[IDP_NAME]) })
    }
  }

  // Not entirely sure if this is the right way to go for tracking the active field
  const setActiveField = (e: any) => {
    setState({ activeField: e.target.name })
  }

  const { identityProvider } = props
  return (
    <>
      {!!state.showDeleteConfirmationOverlay && (
        <DeleteConfirmationOverlay
          onCancel={hideDeleteConfirmation}
          onConfirm={onDelete}
          message={<FormattedMessage {...messages.confirmDeleteTitle} />}
          subMessage={<FormattedMessage {...messages.confirmDeleteMessage} />}
        />
      )}
      <Form
        buttons={
          <>
            <Button color="secondary" type="reset">
              {intl.formatMessage(messages.cancelButton)}
            </Button>
            <Button color="warning" onClick={showDeleteConfirmation}>
              {intl.formatMessage(messages.deleteButton)}
            </Button>
            <Button color="primary" type="submit">
              {intl.formatMessage(messages.updateButton)}
            </Button>
          </>
        }
        initialValues={identityProvider}
        isActionInProgress={props.isActionInProgress}
        onSubmit={onUpdate}
        onReset={onCancel}
        onChange={setFieldsForProvider}
        schema={schema}
        title={<FormattedMessage {...messages.cardTitle} />}
      >
        <Select
          label={<FormattedMessage {...messages.identityProvider} />}
          name="idpName"
          required
          onChange={setActiveField}
        >
          {/* @ts-expect-error */}
          <Select.Option value=""></Select.Option>
          <Select.Option value={AZURE}>
            {intl.formatMessage(messages.identityProviderAzureActiveDirectory)}
          </Select.Option>
          <Select.Option value={OKTA}>{intl.formatMessage(messages.identityProviderOkta)}</Select.Option>
          <Select.Option value={OTHER}>{intl.formatMessage(messages.identityProviderOther)}</Select.Option>
        </Select>
        <Textarea
          description={<FormattedMessage {...messages.metadataDescription} />}
          label={<FormattedMessage {...messages.metadata} />}
          name="metadata"
          placeholder={intl.formatMessage(messages.metadataPlaceholder)}
          required
          rows={15}
        />
        <Text
          label={<FormattedMessage {...messages.emailAttribute} />}
          name="emailAttribute"
          placeholder={intl.formatMessage(messages.emailAttributePlaceholder)}
          required
          disabled={state.disabled}
        />
        <Text
          label={<FormattedMessage {...messages.usernameAttribute} />}
          name="usernameAttribute"
          placeholder={intl.formatMessage(messages.usernameAttributePlaceholder)}
          required
          disabled={state.disabled}
        />
        <Text
          label={<FormattedMessage {...messages.familyNameAttribute} />}
          name="familyNameAttribute"
          placeholder={intl.formatMessage(messages.familyNameAttributePlaceholder)}
          required
          disabled={state.disabled}
        />
      </Form>
    </>
  )
}

EditSamlIdentityProvider.displayName = 'EditSamlIdentityProvider'

EditSamlIdentityProvider.propTypes = {
  identityProvider: PropTypes.shape({
    id: PropTypes.string.isRequired,
    idpName: PropTypes.string.isRequired,
    metadata: PropTypes.string.isRequired,
    emailAttribute: PropTypes.string.isRequired,
    usernameAttribute: PropTypes.string.isRequired,
    familyNameAttribute: PropTypes.string.isRequired,
    idpDomains: PropTypes.array.isRequired
  }).isRequired,

  isActionInProgress: PropTypes.bool.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

export default EditSamlIdentityProvider
